import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CategoryPicker, EmojiPicker, emojiForCategories } from "../community/eventCategories";
import { API } from "../../App";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Plus, ChevronLeft, ChevronRight, Edit, Trash2, Coffee, UserPlus, Users } from "lucide-react";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const DAY_PILLS = [
  { idx: 0, short: "Mon" },
  { idx: 1, short: "Tue" },
  { idx: 2, short: "Wed" },
  { idx: 3, short: "Thu" },
  { idx: 4, short: "Fri" },
  { idx: 5, short: "Sat" },
  { idx: 6, short: "Sun" },
];
const TIME_SLOTS = [
  { id: "morning", label: "Morning", time: "8-12" },
  { id: "midday", label: "Midday", time: "12-15" },
  { id: "afternoon", label: "Afternoon", time: "15-18" },
  { id: "evening", label: "Evening", time: "18+" }
];

const getMonday = () => {
  const now = new Date();
  const dayOfWeek = now.getDay();
  const monday = new Date(now);
  monday.setDate(now.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
  monday.setHours(0, 0, 0, 0);
  return monday;
};

const PendingEvents = ({ headers, onRefresh }) => {
  const [pending, setPending] = useState([]);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/admin/events/pending`, { headers });
        setPending(res.data || []);
      } catch { /* silent */ }
    })();
    const poll = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/admin/events/pending`, { headers });
        setPending(res.data || []);
      } catch { /* silent */ }
    }, 60000);
    return () => clearInterval(poll);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const act = async (id, action) => {
    setBusyId(id);
    try {
      await axios.post(`${API}/admin/events/${id}/${action}`, {}, { headers });
      toast.success(action === "approve" ? "Approved — the whole house has been invited 📣" : "Denied — the guest has been notified");
      setPending((p) => p.filter((e) => e.id !== id));
      onRefresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Something went wrong");
    }
    setBusyId(null);
  };

  if (pending.length === 0) return null;
  return (
    <div className="bg-amber-50 border border-amber-200 rounded-2xl p-3 sm:p-4" data-testid="pending-events-card">
      <h3 className="font-serif text-sm sm:text-base text-[#2E2A26] mb-2">⏳ Events awaiting your approval ({pending.length})</h3>
      <div className="space-y-2">
        {pending.map((e) => (
          <div key={e.id} className="bg-white rounded-xl p-3 flex flex-wrap items-center gap-2" data-testid={`pending-event-${e.id}`}>
            <span className="text-xl">{e.emoji}</span>
            <div className="flex-1 min-w-[140px]">
              <p className="text-sm font-medium text-[#2E2A26]">{e.name}</p>
              <p className="text-[11px] text-[#2E2A26]/55">
                {e.date}{e.time ? ` · ${e.time}` : ""}{e.location ? ` · 📍 ${e.location}` : ""} · by {e.created_by_name || "guest"}
              </p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => act(e.id, "approve")} disabled={busyId === e.id}
                className="px-3 py-1.5 rounded-full bg-[#8C9A7C] text-white text-xs font-medium disabled:opacity-50"
                data-testid={`approve-event-${e.id}`}>Approve</button>
              <button onClick={() => act(e.id, "deny")} disabled={busyId === e.id}
                className="px-3 py-1.5 rounded-full bg-white border border-[#C85A38]/40 text-[#C85A38] text-xs font-medium disabled:opacity-50"
                data-testid={`deny-event-${e.id}`}>Deny</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const ScheduleTab = ({ weeklySchedule, espressoTalks, guests = [], headers, onRefresh }) => {
  const [selectedWeekStart, setSelectedWeekStart] = useState(getMonday);
  const [showScheduleForm, setShowScheduleForm] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  // Mobile portrait: which day index (0-6, Mon=0) the admin is viewing
  const [mobileDay, setMobileDay] = useState(() => {
    const d = new Date().getDay();
    return d === 0 ? 6 : d - 1;
  });
  const [scheduleForm, setScheduleForm] = useState({
    name: "",
    emoji: "🎉",
    categories: [],
    co_host_id: "",
    event_type: "oneoff",
    date: "",
    days_of_week: [0],
    time_slot: "morning",
    time: "09:00",
    location: "Living Room",
    description: ""
  });

  const [editingEspressoTalk, setEditingEspressoTalk] = useState(null);
  const [creatingEspressoTalk, setCreatingEspressoTalk] = useState(false);
  const [attendeeSheet, setAttendeeSheet] = useState(null); // { activity, attendees, loading }

  const openAttendees = async (activity) => {
    setAttendeeSheet({ activity, attendees: [], loading: true });
    try {
      const res = await axios.get(`${API}/community/events/${encodeURIComponent(activity.id)}/attendees`, { headers });
      setAttendeeSheet({ activity, attendees: res.data?.attendees || [], loading: false });
    } catch (error) {
      toast.error("Couldn't load attendees");
      setAttendeeSheet(null);
    }
  };
  const [espressoEditForm, setEspressoEditForm] = useState({
    topic: "",
    description: "",
    date: "",
    time: "",
    location: "",
    duration_minutes: 30,
    host_id: "",
  });

  // Get the dates for the selected week
  const getWeekDates = () => {
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(selectedWeekStart);
      date.setDate(selectedWeekStart.getDate() + i);
      dates.push(date);
    }
    return dates;
  };

  // Get activities for a specific date and time slot
  const getActivitiesForDateSlot = (date, timeSlot) => {
    // Format date as YYYY-MM-DD using local timezone (not UTC)
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    return (weeklySchedule.calendar || []).filter(a => a.date === dateStr && a.time_slot === timeSlot);
  };

  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const goToPreviousWeek = () => {
    const newStart = new Date(selectedWeekStart);
    newStart.setDate(newStart.getDate() - 7);
    setSelectedWeekStart(newStart);
  };

  const goToNextWeek = () => {
    const newStart = new Date(selectedWeekStart);
    newStart.setDate(newStart.getDate() + 7);
    setSelectedWeekStart(newStart);
  };

  const goToThisWeek = () => setSelectedWeekStart(getMonday());

  const formatDateHeader = (date) => {
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  };

  const formatWeekRange = () => {
    const weekEnd = new Date(selectedWeekStart);
    weekEnd.setDate(weekEnd.getDate() + 6);
    return `${selectedWeekStart.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} - ${weekEnd.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`;
  };

  const weekDates = getWeekDates();

  const openEditEspressoTalk = (talk) => {
    setEditingEspressoTalk(talk);
    setCreatingEspressoTalk(false);
    setEspressoEditForm({
      topic: talk.topic,
      description: talk.description || "",
      date: talk.date,
      time: talk.time,
      location: talk.location || "Living Room",
      duration_minutes: talk.duration_minutes || 30,
      host_id: talk.host_id || "",
    });
  };

  const openCreateEspressoTalk = () => {
    setCreatingEspressoTalk(true);
    setEditingEspressoTalk(null);
    // Default to tomorrow at 10:00 so admin only has to pick a host
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const yyyy = tomorrow.getFullYear();
    const mm = String(tomorrow.getMonth() + 1).padStart(2, "0");
    const dd = String(tomorrow.getDate()).padStart(2, "0");
    setEspressoEditForm({
      topic: "",
      description: "",
      date: `${yyyy}-${mm}-${dd}`,
      time: "10:00",
      location: "Living Room",
      duration_minutes: 20,
      host_id: "",
    });
  };

  const saveEspressoTalk = async () => {
    if (!espressoEditForm.topic) {
      toast.error("Give the talk a topic");
      return;
    }
    if (creatingEspressoTalk && !espressoEditForm.host_id) {
      toast.error("Choose a host for this talk");
      return;
    }
    if (!espressoEditForm.date || !espressoEditForm.time) {
      toast.error("Date and time are required");
      return;
    }
    try {
      if (creatingEspressoTalk) {
        await axios.post(`${API}/community/espresso-talks`, espressoEditForm, { headers });
        toast.success("Talk scheduled!");
      } else {
        await axios.put(`${API}/community/espresso-talks/${editingEspressoTalk.id}`, espressoEditForm, { headers });
        toast.success("Talk updated!");
      }
      setEditingEspressoTalk(null);
      setCreatingEspressoTalk(false);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || (creatingEspressoTalk ? "Failed to create talk" : "Failed to update talk"));
    }
  };

  // Edit any activity (one-off event OR recurring template) — opens the form pre-populated.
  const handleEditActivity = (activity) => {
    if (activity.type === "specific") {
      setEditingTemplate({ id: activity.id, _kind: "event" });
      setScheduleForm({
        name: activity.name,
        emoji: activity.emoji,
        event_type: "oneoff",
        date: activity.date,
        days_of_week: [],
        time_slot: activity.time_slot,
        time: activity.time || "09:00",
        location: activity.location || "Living Room",
        description: activity.description || "",
        categories: activity.categories || [],
        co_host_id: activity.co_host_id || "",
      });
      setShowScheduleForm(true);
      return;
    }
    const template = (weeklySchedule.templates || []).find(t => t.id === activity.template_id);
    if (template) {
      const days = Array.isArray(template.days_of_week) && template.days_of_week.length > 0
        ? template.days_of_week
        : (typeof template.day_of_week === "number" ? [template.day_of_week] : []);
      setEditingTemplate(template);
      setScheduleForm({
        name: template.name,
        emoji: template.emoji,
        categories: template.categories || [],
        co_host_id: "",
        event_type: "weekly",
        date: "",
        days_of_week: days,
        time_slot: template.time_slot,
        time: template.time || "09:00",
        location: template.location || "Living Room",
        description: template.description || ""
      });
      setShowScheduleForm(true);
    }
  };

  // Delete any activity — espresso talks need their own endpoint.
  const handleDeleteActivity = async (activity) => {
    try {
      if (activity.type === "espresso") {
        await axios.delete(`${API}/community/espresso-talks/${activity.id}`, { headers });
        toast.success("Espresso Talk deleted");
        onRefresh();
        return;
      }
      if (activity.type === "specific") {
        await axios.delete(`${API}/community/events/${activity.id}`, { headers });
        toast.success("Event deleted");
        onRefresh();
        return;
      }
      if (activity.template_id) {
        await axios.delete(`${API}/community/templates/${activity.template_id}`, { headers });
        toast.success("Activity deleted");
        onRefresh();
        return;
      }
      toast.error("Cannot delete - no template ID");
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Failed to delete");
    }
  };

  return (
    <>
          <div className="space-y-6">
            <PendingEvents headers={headers} onRefresh={onRefresh} />
            {/* Calendar Header with Navigation — slim chrome on mobile */}
            <div className="bg-gradient-to-br from-[#3D4035] to-[#5D6055] rounded-2xl sm:rounded-3xl p-3 sm:p-5 text-white">
              <div className="flex items-center justify-between gap-2 mb-2 sm:mb-3">
                <h3 className="font-serif text-base sm:text-xl truncate">
                  <span className="sm:hidden">Calendar 📅</span>
                  <span className="hidden sm:inline">Savi Calendar 📅</span>
                </h3>
                <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
                  <button 
                    onClick={goToThisWeek}
                    className="text-[11px] sm:text-xs bg-white/20 px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full hover:bg-white/30 transition-colors"
                  >
                    Today
                  </button>
                  <Button
                    data-testid="add-activity-btn"
                    onClick={() => {
                      setEditingTemplate(null);
                      setScheduleForm({
                        name: "",
                        emoji: "🎉",
                        categories: [],
                        co_host_id: "",
                        event_type: "oneoff",
                        date: "",
                        days_of_week: [0],
                        time_slot: "morning",
                        time: "09:00",
                        location: "Living Room",
                        description: ""
                      });
                      setShowScheduleForm(true);
                    }}
                    className="bg-white text-[#3D4035] hover:bg-white/90 text-[11px] sm:text-sm h-7 sm:h-9 px-2.5 sm:px-3"
                    size="sm"
                  >
                    <Plus className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
                    <span className="hidden sm:inline">Add Activity</span>
                    <span className="sm:hidden ml-0.5">Add</span>
                  </Button>
                </div>
              </div>
              
              {/* Week Navigation */}
              <div className="flex items-center justify-between">
                <button 
                  onClick={goToPreviousWeek}
                  data-testid="schedule-prev-week"
                  className="p-1.5 sm:p-2 rounded-full hover:bg-white/20 transition-colors"
                >
                  <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>
                <span className="font-medium text-xs sm:text-base">{formatWeekRange()}</span>
                <button 
                  onClick={goToNextWeek}
                  data-testid="schedule-next-week"
                  className="p-1.5 sm:p-2 rounded-full hover:bg-white/20 transition-colors"
                >
                  <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>
              </div>
            </div>

            {/* Add/Edit Template Form */}
            {showScheduleForm && (
              <div className="bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 border border-stone-100">
                <h3 className="font-medium text-[#3D4035] mb-4 text-sm sm:text-base">
                  {editingTemplate ? "Edit Recurring Activity" : "Add to Calendar"}
                </h3>
                {!editingTemplate && (
                  <div className="flex gap-2 mb-4">
                    <button
                      onClick={() => setScheduleForm({ ...scheduleForm, event_type: "oneoff" })}
                      className={`flex-1 py-2 px-3 rounded-xl text-sm font-medium border transition-colors ${
                        scheduleForm.event_type === "oneoff"
                          ? "bg-[#3D4035] text-white border-[#3D4035]"
                          : "bg-white text-[#3D4035]/60 border-stone-200 hover:border-stone-300"
                      }`}
                      data-testid="event-type-oneoff"
                    >
                      📅 One-off event
                    </button>
                    <button
                      onClick={() => setScheduleForm({ ...scheduleForm, event_type: "recurring" })}
                      className={`flex-1 py-2 px-3 rounded-xl text-sm font-medium border transition-colors ${
                        scheduleForm.event_type === "recurring"
                          ? "bg-[#3D4035] text-white border-[#3D4035]"
                          : "bg-white text-[#3D4035]/60 border-stone-200 hover:border-stone-300"
                      }`}
                      data-testid="event-type-recurring"
                    >
                      🔁 Repeats weekly
                    </button>
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <label className="block text-sm text-[#3D4035]/60 mb-1">Activity Name</label>
                    <input
                      type="text"
                      value={scheduleForm.name}
                      onChange={(e) => setScheduleForm({ ...scheduleForm, name: e.target.value })}
                      placeholder="e.g., Yoga, Hiking"
                      className="w-full px-3 py-2 border border-stone-200 rounded-xl text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-[#3D4035]/60 mb-1">Categories <span className="text-[#3D4035]/40">(1–3)</span></label>
                    <CategoryPicker
                      value={scheduleForm.categories || []}
                      onChange={(cats) => { setScheduleForm({ ...scheduleForm, categories: cats, emoji: scheduleForm._emojiTouched ? scheduleForm.emoji : emojiForCategories(cats) }); }}
                      testidPrefix="admin-event-category"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-[#3D4035]/60 mb-1">Icon <span className="text-[#3D4035]/40">(auto from the first category)</span></label>
                    <EmojiPicker value={scheduleForm.emoji} onChange={(em) => setScheduleForm({ ...scheduleForm, emoji: em, _emojiTouched: true })} testidPrefix="admin-event-emoji" />
                  </div>
                  {scheduleForm.event_type === "oneoff" && (
                    <div>
                      <label className="block text-sm text-[#3D4035]/60 mb-1">Co-host <span className="text-[#3D4035]/40">(optional)</span></label>
                      <select
                        value={scheduleForm.co_host_id || ""}
                        onChange={(e) => setScheduleForm({ ...scheduleForm, co_host_id: e.target.value })}
                        className="w-full px-3 py-2 border border-stone-200 rounded-xl text-sm"
                        data-testid="admin-event-cohost"
                      >
                        <option value="">— No co-host —</option>
                        {guests
                          .filter((g) => !g.archived)
                          .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
                          .map((g) => (
                            <option key={g.id} value={g.id}>{g.name}{g.role === "admin" ? " (staff)" : ""}</option>
                          ))}
                      </select>
                    </div>
                  )}
                  {scheduleForm.event_type === "oneoff" && !editingTemplate ? (
                    <div>
                      <label className="block text-sm text-[#3D4035]/60 mb-1">Date</label>
                      <input
                        type="date"
                        value={scheduleForm.date}
                        onChange={(e) => setScheduleForm({ ...scheduleForm, date: e.target.value })}
                        className="w-full px-3 py-2 border border-stone-200 rounded-xl text-sm"
                        data-testid="event-date-input"
                      />
                    </div>
                  ) : (
                    <div className="sm:col-span-2">
                      <label className="block text-sm text-[#3D4035]/60 mb-2">Repeats on</label>
                      <div className="flex flex-wrap gap-1.5 mb-2" data-testid="day-pills">
                        {DAY_PILLS.map((d) => {
                          const selected = (scheduleForm.days_of_week || []).includes(d.idx);
                          return (
                            <button
                              key={d.idx}
                              type="button"
                              data-testid={`day-pill-${d.short.toLowerCase()}`}
                              onClick={() => {
                                const current = scheduleForm.days_of_week || [];
                                const next = selected
                                  ? current.filter((x) => x !== d.idx)
                                  : [...current, d.idx].sort((a, b) => a - b);
                                setScheduleForm({ ...scheduleForm, days_of_week: next });
                              }}
                              className={`w-10 h-10 rounded-full text-xs font-medium border transition-colors ${
                                selected
                                  ? "bg-[#3D4035] text-white border-[#3D4035]"
                                  : "bg-white text-[#3D4035]/70 border-stone-200 hover:border-stone-400"
                              }`}
                            >
                              {d.short.slice(0, 1)}
                            </button>
                          );
                        })}
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        <button
                          type="button"
                          data-testid="day-preset-all"
                          onClick={() => setScheduleForm({ ...scheduleForm, days_of_week: [0, 1, 2, 3, 4, 5, 6] })}
                          className="text-[11px] px-3 py-1 rounded-full bg-[#F4F1DE] text-[#3D4035] hover:bg-[#E8E4D0] border border-stone-200"
                        >
                          All days
                        </button>
                        <button
                          type="button"
                          data-testid="day-preset-weekdays"
                          onClick={() => setScheduleForm({ ...scheduleForm, days_of_week: [0, 1, 2, 3, 4] })}
                          className="text-[11px] px-3 py-1 rounded-full bg-[#F4F1DE] text-[#3D4035] hover:bg-[#E8E4D0] border border-stone-200"
                        >
                          Weekdays
                        </button>
                        <button
                          type="button"
                          data-testid="day-preset-weekend"
                          onClick={() => setScheduleForm({ ...scheduleForm, days_of_week: [5, 6] })}
                          className="text-[11px] px-3 py-1 rounded-full bg-[#F4F1DE] text-[#3D4035] hover:bg-[#E8E4D0] border border-stone-200"
                        >
                          Weekend
                        </button>
                        <button
                          type="button"
                          data-testid="day-preset-clear"
                          onClick={() => setScheduleForm({ ...scheduleForm, days_of_week: [] })}
                          className="text-[11px] px-3 py-1 rounded-full bg-white text-[#3D4035]/60 hover:bg-stone-50 border border-stone-200"
                        >
                          Clear
                        </button>
                      </div>
                      {(scheduleForm.days_of_week || []).length === 0 && (
                        <p className="text-[11px] text-red-500 mt-2">Pick at least one day</p>
                      )}
                    </div>
                  )}
                  <div>
                    <label className="block text-sm text-[#3D4035]/60 mb-1">Time Slot</label>
                    <select
                      value={scheduleForm.time_slot}
                      onChange={(e) => setScheduleForm({ ...scheduleForm, time_slot: e.target.value })}
                      className="w-full px-3 py-2 border border-stone-200 rounded-xl text-sm"
                    >
                      <option value="morning">Morning (8-12)</option>
                      <option value="midday">Midday (12-15)</option>
                      <option value="afternoon">Afternoon (15-18)</option>
                      <option value="evening">Evening (18+)</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-[#3D4035]/60 mb-1">Time</label>
                    <input
                      type="time"
                      value={scheduleForm.time}
                      onChange={(e) => setScheduleForm({ ...scheduleForm, time: e.target.value })}
                      className="w-full px-3 py-2 border border-stone-200 rounded-xl text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-[#3D4035]/60 mb-1">Location</label>
                    <select
                      value={scheduleForm.location}
                      onChange={(e) => setScheduleForm({ ...scheduleForm, location: e.target.value })}
                      className="w-full px-3 py-2 border border-stone-200 rounded-xl text-sm"
                    >
                      <option value="Living Room">Living Room</option>
                      <option value="Dining Room">Dining Room</option>
                      <option value="Kitchen">Kitchen</option>
                      <option value="Pool Area">Pool Area</option>
                      <option value="Garden">Garden</option>
                      <option value="Terrace">Terrace</option>
                      <option value="Driveway">Driveway</option>
                      <option value="Gym">Gym</option>
                      <option value="Sierra Calderona">Sierra Calderona</option>
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-sm text-[#3D4035]/60 mb-1">Description (optional)</label>
                    <input
                      type="text"
                      value={scheduleForm.description}
                      onChange={(e) => setScheduleForm({ ...scheduleForm, description: e.target.value })}
                      placeholder="Brief description"
                      className="w-full px-3 py-2 border border-stone-200 rounded-xl text-sm"
                    />
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-2 mt-4">
                  <Button
                    onClick={async () => {
                      if (!scheduleForm.name) {
                        toast.error("Give the activity a name");
                        return;
                      }
                      try {
                        if (editingTemplate && editingTemplate._kind === "event") {
                          // EDITING a one-off scheduled event
                          if (!scheduleForm.date) {
                            toast.error("Pick a date for the event");
                            return;
                          }
                          if (!(scheduleForm.categories || []).length) { toast.error("Pick at least one category"); return; }
                          await axios.put(`${API}/community/events/${editingTemplate.id}`, {
                            name: scheduleForm.name,
                            emoji: scheduleForm.emoji,
                            date: scheduleForm.date,
                            time_slot: scheduleForm.time_slot,
                            time: scheduleForm.time,
                            location: scheduleForm.location,
                            description: scheduleForm.description,
                            categories: scheduleForm.categories,
                            co_host_id: scheduleForm.co_host_id || null,
                          }, { headers });
                          toast.success("Event updated!");
                        } else if (editingTemplate) {
                          if (!scheduleForm.days_of_week || scheduleForm.days_of_week.length === 0) {
                            toast.error("Pick at least one day");
                            return;
                          }
                          await axios.put(`${API}/community/templates/${editingTemplate.id}`, scheduleForm, { headers });
                          toast.success("Activity updated!");
                        } else if (scheduleForm.event_type === "oneoff") {
                          if (!scheduleForm.date) {
                            toast.error("Pick a date for the event");
                            return;
                          }
                          if (!(scheduleForm.categories || []).length) { toast.error("Pick at least one category"); return; }
                          await axios.post(`${API}/community/events`, {
                            name: scheduleForm.name,
                            emoji: scheduleForm.emoji,
                            date: scheduleForm.date,
                            time_slot: scheduleForm.time_slot,
                            time: scheduleForm.time,
                            location: scheduleForm.location,
                            description: scheduleForm.description,
                            categories: scheduleForm.categories,
                            co_host_id: scheduleForm.co_host_id || null,
                          }, { headers });
                          toast.success("Event added to the calendar! 🎉");
                        } else {
                          if (!scheduleForm.days_of_week || scheduleForm.days_of_week.length === 0) {
                            toast.error("Pick at least one day");
                            return;
                          }
                          await axios.post(`${API}/community/templates`, scheduleForm, { headers });
                          toast.success("Weekly activity added!");
                        }
                        setShowScheduleForm(false);
                        setEditingTemplate(null);
                        onRefresh();
                      } catch (error) {
                        toast.error("Failed to save");
                      }
                    }}
                    className="bg-[#81B29A] hover:bg-[#6A9A82] text-white"
                    data-testid="save-activity-btn"
                  >
                    {editingTemplate ? "Update Activity" : scheduleForm.event_type === "oneoff" ? "Add Event" : "Add Weekly Activity"}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowScheduleForm(false);
                      setEditingTemplate(null);
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}

            {/* Weekly Calendar — desktop / landscape grid */}
            <div className="hidden sm:block bg-white rounded-2xl sm:rounded-3xl p-3 sm:p-6 border border-stone-100" data-testid="admin-schedule-grid-landscape">
              <div className="overflow-x-auto -mx-3 px-3 sm:mx-0 sm:px-0">
                <table className="w-full min-w-[600px]">
                  <thead>
                    <tr>
                      <th className="p-2 text-left text-xs text-[#3D4035]/60 w-24"></th>
                      {weekDates.map((date, idx) => (
                        <th 
                          key={idx} 
                          className={`p-2 text-center ${isToday(date) ? 'bg-[#E07A5F]/10 rounded-t-xl' : ''}`}
                        >
                          <div className={`text-sm font-medium ${isToday(date) ? 'text-[#E07A5F]' : 'text-[#3D4035]'}`}>
                            {DAYS[idx].slice(0, 3)}
                          </div>
                          <div className={`text-xs ${isToday(date) ? 'text-[#E07A5F] font-bold' : 'text-[#3D4035]/60'}`}>
                            {formatDateHeader(date)}
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {TIME_SLOTS.map(slot => (
                      <tr key={slot.id} className="border-t border-stone-100">
                        <td className="p-2 text-xs text-[#3D4035]/60 align-top">
                          <div className="font-medium">{slot.label}</div>
                          <div className="text-[10px]">{slot.time}</div>
                        </td>
                        {weekDates.map((date, idx) => {
                          const activities = getActivitiesForDateSlot(date, slot.id);
                          return (
                            <td 
                              key={`${idx}-${slot.id}`} 
                              className={`p-1 align-top min-w-[100px] ${isToday(date) ? 'bg-[#E07A5F]/5' : ''}`}
                            >
                              {activities.map((activity, aidx) => (
                                <div 
                                  key={aidx}
                                  className={`rounded-lg p-2 mb-1 text-center group relative ${
                                    activity.type === 'espresso'
                                      ? 'bg-[#8C9A7C]/25 hover:bg-[#8C9A7C]/40'
                                      : activity.type === 'specific' 
                                      ? 'bg-[#E07A5F]/20 hover:bg-[#E07A5F]/30' 
                                      : 'bg-[#F4F1DE] hover:bg-[#E8E4D0]'
                                  }`}
                                >
                                  <span className="text-lg block">{activity.emoji}</span>
                                  <span className="text-[10px] text-[#3D4035] font-medium line-clamp-2">{activity.name}</span>
                                  {activity.time && (
                                    <span className="text-[9px] text-[#3D4035]/60 block">{activity.time}</span>
                                  )}
                                  {activity.attendee_count > 0 && (
                                    <button
                                      type="button"
                                      onClick={(e) => { e.stopPropagation(); openAttendees(activity); }}
                                      className="mt-1 inline-flex items-center gap-1 text-[9px] text-[#3D4035]/75 hover:text-[#3D4035] hover:underline"
                                      data-testid={`admin-attendee-count-${activity.id}`}
                                    >
                                      <Users className="w-2.5 h-2.5" />
                                      {activity.attendee_count} going
                                    </button>
                                  )}
                                  {/* Edit/Delete buttons — espresso talks use their dedicated dialog instead of the generic form */}
                                  <div className="absolute -top-2 -right-2 flex gap-1">
                                    {activity.type === "espresso" ? (
                                      <button
                                        onClick={() => {
                                          const talk = espressoTalks.find((t) => t.id === activity.id);
                                          if (talk) openEditEspressoTalk(talk);
                                        }}
                                        className="p-1.5 bg-[#3D4035] rounded-full shadow text-white hover:bg-[#5D6055]"
                                        title="Edit espresso talk"
                                      >
                                        <Edit className="w-3 h-3" />
                                      </button>
                                    ) : (
                                      <button
                                        onClick={() => handleEditActivity(activity)}
                                        className="p-1.5 bg-[#3D4035] rounded-full shadow text-white hover:bg-[#5D6055]"
                                        title="Edit"
                                      >
                                        <Edit className="w-3 h-3" />
                                      </button>
                                    )}
                                    <button
                                      onClick={(e) => { e.stopPropagation(); handleDeleteActivity(activity); }}
                                      className="p-1.5 bg-red-500 rounded-full shadow text-white hover:bg-red-600"
                                      title="Delete"
                                    >
                                      <Trash2 className="w-3 h-3" />
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* Legend */}
              <div className="flex flex-wrap gap-4 justify-center text-xs mt-4 pt-4 border-t border-stone-100">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded bg-[#F4F1DE]"></div>
                  <span className="text-[#3D4035]/60">Weekly Recurring</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded bg-[#E07A5F]/20"></div>
                  <span className="text-[#3D4035]/60">Special Event</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded bg-[#8C9A7C]/25"></div>
                  <span className="text-[#3D4035]/60">Espresso Talk</span>
                </div>
              </div>
            </div>

            {/* Weekly Calendar — mobile portrait (day pill picker + single-day list). Landscape phones (sm:) get the grid above. */}
            <div className="sm:hidden space-y-2.5" data-testid="admin-schedule-mobile-stack">
              {/* Day pills */}
              <div className="bg-white rounded-2xl border border-stone-100 p-1.5">
                <div className="grid grid-cols-7 gap-0.5">
                  {weekDates.map((date, idx) => {
                    const selected = idx === mobileDay;
                    const today = isToday(date);
                    return (
                      <button
                        key={idx}
                        onClick={() => setMobileDay(idx)}
                        className={`flex flex-col items-center py-1 rounded-xl text-[10px] transition-colors ${
                          selected
                            ? "bg-[#3D4035] text-white"
                            : "text-[#2E2A26] hover:bg-[#F4ECE2]"
                        }`}
                        data-testid={`admin-day-pill-${idx}`}
                      >
                        <span className="text-[9px] tracking-[0.12em]">{DAYS[idx].slice(0,3).toUpperCase()}</span>
                        <span className={`text-sm font-medium ${today && !selected ? "text-[#C85A38]" : ""}`}>
                          {date.getDate()}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Selected day events */}
              {(() => {
                const date = weekDates[mobileDay];
                const dayName = DAYS[mobileDay];
                const today = isToday(date);
                const allDay = TIME_SLOTS.flatMap((slot) =>
                  getActivitiesForDateSlot(date, slot.id).map((a) => ({ ...a, _slot: slot })),
                );
                return (
                  <div
                    className={`rounded-2xl border ${today ? "border-[#E07A5F]/60 bg-[#FFF3EE]" : "border-stone-100 bg-white"} overflow-hidden`}
                    data-testid={`admin-schedule-day-${mobileDay}`}
                  >
                    <div className={`px-3 py-2 flex items-baseline justify-between ${today ? "bg-[#E07A5F]/10" : "bg-[#FBF7F1]"}`}>
                      <div className="flex items-baseline gap-2">
                        <span
                          className={`font-medium ${today ? "text-[#C85A38]" : "text-[#2E2A26]"}`}
                          style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "1.15rem" }}
                        >
                          {dayName}
                        </span>
                        {today && (
                          <span className="text-[9px] uppercase tracking-wider text-[#C85A38] font-medium">Today</span>
                        )}
                      </div>
                      <span className="text-[11px] text-[#2E2A26]/55">{formatDateHeader(date)}</span>
                    </div>
                    {allDay.length === 0 ? (
                      <div className="px-3 py-5 text-[11px] text-[#2E2A26]/35 italic text-center">Nothing scheduled — tap Add above to fill the day.</div>
                    ) : (
                      <div className="px-2 py-1.5 space-y-1">
                        {allDay.map((a, ai) => (
                          <div
                            key={a.id + ai}
                            className={`w-full rounded-xl px-2.5 py-2 flex items-center gap-2.5 ${
                              a.type === "espresso"
                                ? "bg-[#8C9A7C]/25"
                                : a.type === "specific"
                                ? "bg-[#E07A5F]/15"
                                : "bg-[#F4F1DE]"
                            }`}
                            data-testid={`admin-event-row-mobile-${a.id}`}
                          >
                            <span className="text-xl">{a.emoji || "•"}</span>
                            <div className="flex-1 min-w-0">
                              <div className="text-[13px] text-[#2E2A26] font-medium leading-tight truncate">
                                {a.name}
                              </div>
                              <div className="text-[10px] text-[#2E2A26]/55 mt-0.5 truncate">
                                {a.time}
                                {a.time && a.location ? " · " : ""}
                                {a.location && `📍 ${a.location}`}
                              </div>
                              {a.attendee_count > 0 && (
                                <button
                                  type="button"
                                  onClick={() => openAttendees(a)}
                                  className="text-[10px] text-[#3D4035]/75 hover:text-[#3D4035] underline mt-0.5 inline-flex items-center gap-1"
                                  data-testid={`admin-attendee-count-mobile-${a.id}`}
                                >
                                  <Users className="w-2.5 h-2.5" />
                                  {a.attendee_count} going
                                </button>
                              )}
                            </div>
                            <div className="flex flex-col gap-1 flex-shrink-0">
                              <button
                                onClick={() => {
                                  if (a.type === "espresso") {
                                    const talk = espressoTalks.find((t) => t.id === a.id);
                                    if (talk) openEditEspressoTalk(talk);
                                  } else {
                                    handleEditActivity(a);
                                  }
                                }}
                                className="p-1.5 bg-[#3D4035] rounded-full text-white"
                                title="Edit"
                                data-testid={`admin-edit-mobile-${a.id}`}
                              >
                                <Edit className="w-3 h-3" />
                              </button>
                              <button
                                onClick={() => handleDeleteActivity(a)}
                                className="p-1.5 bg-red-500 rounded-full text-white"
                                title="Delete"
                                data-testid={`admin-delete-mobile-${a.id}`}
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Mobile legend (compact) */}
              <div className="flex flex-wrap gap-3 justify-center text-[10px]">
                <span className="flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded bg-[#F4F1DE]"></span>
                  <span className="text-[#3D4035]/60">Weekly</span>
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded bg-[#E07A5F]/20"></span>
                  <span className="text-[#3D4035]/60">Special</span>
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded bg-[#8C9A7C]/25"></span>
                  <span className="text-[#3D4035]/60">Espresso</span>
                </span>
              </div>

              {/* Rotate hint */}
              <div className="text-center text-[10px] text-[#2E2A26]/40 italic">
                Rotate your phone for the full week →
              </div>
            </div>

            {/* Upcoming Espresso Talks */}
            <div className="bg-white rounded-3xl p-6 border border-stone-100">
              <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
                <div>
                  <h3 className="font-medium text-[#3D4035]">Upcoming Espresso Talks ☕</h3>
                  <p className="text-xs text-[#3D4035]/60">Guest-hosted talks. Add one on a guest&apos;s behalf, or edit / delete existing ones.</p>
                </div>
                <Button
                  size="sm"
                  onClick={openCreateEspressoTalk}
                  className="bg-[#E07A5F] hover:bg-[#D06A4F] text-white rounded-full"
                  data-testid="admin-add-espresso-talk-btn"
                >
                  <UserPlus className="w-4 h-4 mr-1" />
                  Add talk for guest
                </Button>
              </div>
              {espressoTalks.length > 0 ? (
                <div className="space-y-3">
                  {espressoTalks.map((talk) => (
                    <div key={talk.id} className="flex items-center justify-between bg-[#F4F1DE] rounded-xl p-4">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">☕</span>
                        <div>
                          <p className="font-medium text-[#3D4035]">{talk.topic}</p>
                          <p className="text-xs text-[#3D4035]/60">
                            {talk.date} at {talk.time} • {talk.location} • by {talk.host_name}
                          </p>
                          {talk.attendees && talk.attendees.length > 0 && (
                            <p className="text-xs text-[#81B29A] mt-1">{talk.attendees.length} attending</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditEspressoTalk(talk)}
                          className="text-blue-500 hover:text-blue-700 hover:bg-blue-50"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={async () => {
                            if (confirm("Delete this Espresso Talk?")) {
                              try {
                                await axios.delete(`${API}/community/espresso-talks/${talk.id}`, { headers });
                                toast.success("Talk deleted");
                                onRefresh();
                              } catch (error) {
                                toast.error("Failed to delete");
                              }
                            }
                          }}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Coffee className="w-12 h-12 text-[#3D4035]/20 mx-auto mb-2" />
                  <p className="text-[#3D4035]/60">No upcoming Espresso Talks</p>
                  <p className="text-xs text-[#3D4035]/40">Guests can schedule these from the Community page</p>
                </div>
              )}
            </div>
          </div>

      {/* Espresso Talk Create / Edit Dialog */}
      <Dialog
        open={!!editingEspressoTalk || creatingEspressoTalk}
        onOpenChange={(open) => {
          if (!open) {
            setEditingEspressoTalk(null);
            setCreatingEspressoTalk(false);
          }
        }}
      >
        <DialogContent className="bg-[#FDFCF8] max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-[#3D4035]">
              {creatingEspressoTalk ? "Schedule talk for a guest ☕" : "Edit Espresso Talk ☕"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            {/* Host selector — admin can assign / reassign on edit too */}
            <div>
              <Label>Host (the guest leading the talk)</Label>
              <select
                value={espressoEditForm.host_id || ""}
                onChange={(e) => setEspressoEditForm({ ...espressoEditForm, host_id: e.target.value })}
                className="w-full h-10 px-3 rounded-xl border border-stone-200 text-sm mt-1 bg-white"
                data-testid="espresso-host-select"
              >
                <option value="">— Choose a guest —</option>
                {guests.map((g) => (
                  <option key={g.id} value={g.id}>{g.name}</option>
                ))}
              </select>
              {!creatingEspressoTalk && editingEspressoTalk?.host_name && (
                <p className="text-[11px] text-[#3D4035]/55 mt-1">Currently hosted by {editingEspressoTalk.host_name}</p>
              )}
            </div>
            <div>
              <Label>Topic</Label>
              <Input
                value={espressoEditForm.topic}
                onChange={(e) => setEspressoEditForm({ ...espressoEditForm, topic: e.target.value })}
                placeholder="e.g. Bali surf spots cheatsheet"
                className="rounded-xl mt-1"
                data-testid="espresso-topic-input"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={espressoEditForm.description}
                onChange={(e) => setEspressoEditForm({ ...espressoEditForm, description: e.target.value })}
                placeholder="What guests should expect — bring a notebook, etc."
                className="rounded-xl mt-1"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Date</Label>
                <Input
                  type="date"
                  value={espressoEditForm.date}
                  onChange={(e) => setEspressoEditForm({ ...espressoEditForm, date: e.target.value })}
                  className="rounded-xl mt-1"
                />
              </div>
              <div>
                <Label>Time</Label>
                <Input
                  type="time"
                  value={espressoEditForm.time}
                  onChange={(e) => setEspressoEditForm({ ...espressoEditForm, time: e.target.value })}
                  className="rounded-xl mt-1"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Location</Label>
                <Input
                  value={espressoEditForm.location}
                  onChange={(e) => setEspressoEditForm({ ...espressoEditForm, location: e.target.value })}
                  className="rounded-xl mt-1"
                />
              </div>
              <div>
                <Label>Duration (mins)</Label>
                <Input
                  type="number"
                  value={espressoEditForm.duration_minutes}
                  onChange={(e) => setEspressoEditForm({ ...espressoEditForm, duration_minutes: parseInt(e.target.value) })}
                  className="rounded-xl mt-1"
                />
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <Button
                onClick={() => { setEditingEspressoTalk(null); setCreatingEspressoTalk(false); }}
                variant="outline"
                className="flex-1 rounded-full"
              >
                Cancel
              </Button>
              <Button
                onClick={saveEspressoTalk}
                className="flex-1 rounded-full bg-[#E07A5F] hover:bg-[#D06A4F]"
                data-testid="espresso-save-btn"
              >
                {creatingEspressoTalk ? "Schedule talk" : "Save Changes"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Admin attendee sheet — shows everyone who RSVP'd to a calendar event */}
      <Dialog open={!!attendeeSheet} onOpenChange={(open) => !open && setAttendeeSheet(null)}>
        <DialogContent className="bg-[#FDFCF8] max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-[#3D4035] flex items-center gap-2">
              <span className="text-2xl">{attendeeSheet?.activity?.emoji}</span>
              {attendeeSheet?.activity?.name}
            </DialogTitle>
            {(attendeeSheet?.activity?.time || attendeeSheet?.activity?.location) && (
              <p className="text-xs text-[#3D4035]/60">
                {attendeeSheet?.activity?.time && `🕒 ${attendeeSheet?.activity?.time}`}
                {attendeeSheet?.activity?.location && `  ·  📍 ${attendeeSheet?.activity?.location}`}
              </p>
            )}
          </DialogHeader>
          <div className="pt-3">
            <p className="text-xs font-medium uppercase tracking-wider text-[#3D4035]/55 mb-3">
              Going ({attendeeSheet?.attendees?.length || 0})
            </p>
            {attendeeSheet?.loading ? (
              <p className="text-sm text-[#3D4035]/60">Loading…</p>
            ) : (attendeeSheet?.attendees?.length || 0) === 0 ? (
              <p className="text-sm italic text-[#3D4035]/55">Nobody&apos;s RSVP&apos;d yet.</p>
            ) : (
              <ul className="space-y-2 max-h-[55vh] overflow-y-auto">
                {attendeeSheet.attendees.map((a) => (
                  <li key={a.user_id} className="flex items-center gap-3 bg-stone-50 rounded-xl p-2.5">
                    {a.user_avatar ? (
                      <img
                        src={a.user_avatar.startsWith("/") ? `${API.replace(/\/api$/, "")}${a.user_avatar}` : a.user_avatar}
                        alt={a.user_name}
                        className="w-9 h-9 rounded-full object-cover bg-stone-200"
                      />
                    ) : (
                      <div className="w-9 h-9 rounded-full bg-[#F4F1DE] flex items-center justify-center text-sm font-serif text-[#3D4035]">
                        {(a.user_name || "?").charAt(0)}
                      </div>
                    )}
                    <span className="text-sm font-medium text-[#3D4035]">{a.user_name}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ScheduleTab;
